/**
 * <collapsible-group>
 *
 * Toggle for a group block with "Collapse with 'Read more' button" turned on.
 * The group's `.group-block-content` is clipped to a visible height in CSS
 * (snippets/group.liquid) with a fade into the background; this element only
 * animates it open and closed and keeps the button state in sync.
 *
 * Like <group-carousel>, it is rendered as a *sibling* of the content rather
 * than a wrapper, so it finds the content by looking back at its parent.
 *
 * Markup contract (rendered by snippets/group.liquid):
 *   [data-collapsible-content] -> the clipped content, a direct child of the same parent
 *   button[aria-expanded]       -> the toggle, with data-expand-label / data-collapse-label
 *   [data-collapsible-label]    -> the label text inside the toggle
 */

/** Longest the open/close animation can take before we finish it ourselves, in ms. */
const TRANSITION_FALLBACK = 700;

class CollapsibleGroup extends HTMLElement {
  /** @type {HTMLElement | null} */
  #content = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #animating = false;

  connectedCallback() {
    const content = this.parentElement?.querySelector(':scope > [data-collapsible-content]');
    const button = this.querySelector('button[aria-expanded]');
    if (!(content instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) return;

    this.#content = content;
    this.#button = button;

    button.addEventListener('click', this.#onClick);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);

    // Text wrapping, images loading and editor changes all move the content's
    // height, so re-check whether the toggle is needed whenever it resizes.
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(content);
    for (const child of content.children) this.#resizeObserver.observe(child);

    this.#measure();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.#resizeObserver?.disconnect();
  }

  get #expanded() {
    return this.#button?.getAttribute('aria-expanded') === 'true';
  }

  /** Hide the toggle (and the fade) when everything already fits in the visible height. */
  #measure() {
    const content = this.#content;
    if (!content || this.#animating || this.#expanded) return;

    const fits = content.scrollHeight <= content.clientHeight + 1;
    content.classList.toggle('is-fitting', fits);
    this.hidden = fits;

    // When the visible height is 0 nothing inside can be seen, so keep keyboard
    // focus and screen readers out of it too until it's opened.
    content.inert = !fits && content.clientHeight < 1;
  }

  #onClick = () => {
    this.#setExpanded(!this.#expanded);
  };

  /** In the theme editor, open the group when a block inside it is selected so it can be edited. */
  #onBlockSelect = (/** @type {Event} */ event) => {
    if (event.target instanceof Node && this.#content?.contains(event.target)) {
      this.#setExpanded(true, false);
    }
  };

  /**
   * @param {boolean} expand
   * @param {boolean} [animate]
   */
  #setExpanded(expand, animate = true) {
    const content = this.#content;
    const button = this.#button;
    if (!content || !button || expand === this.#expanded) return;

    button.setAttribute('aria-expanded', String(expand));
    const label = button.querySelector('[data-collapsible-label]');
    if (label) label.textContent = (expand ? button.dataset.collapseLabel : button.dataset.expandLabel) ?? '';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduceMotion) {
      content.classList.toggle('is-expanded', expand);
      content.style.maxHeight = '';
      content.inert = false;
      if (!expand) this.#keepInView();
      this.#measure();
      return;
    }

    this.#animating = true;

    if (expand) {
      content.inert = false;
      // Animate from the clipped height to the full height, then drop the cap
      // entirely so later content changes are never cut off.
      content.style.maxHeight = `${content.clientHeight}px`;
      content.getBoundingClientRect();
      content.style.maxHeight = `${content.scrollHeight}px`;
      this.#afterTransition(() => {
        content.classList.add('is-expanded');
        content.style.maxHeight = '';
      });
    } else {
      // Pin the current full height, then release it back to the CSS cap.
      content.style.maxHeight = `${content.scrollHeight}px`;
      content.classList.remove('is-expanded');
      content.getBoundingClientRect();
      content.style.maxHeight = '';
      this.#keepInView();
      this.#afterTransition(() => {});
    }
  }

  /** @param {() => void} done */
  #afterTransition(done) {
    const content = this.#content;
    if (!content) return;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      content.removeEventListener('transitionend', onEnd);
      done();
      this.#animating = false;
      this.#measure();
    };
    const onEnd = (/** @type {TransitionEvent} */ event) => {
      if (event.target === content && event.propertyName === 'max-height') finish();
    };

    content.addEventListener('transitionend', onEnd);
    setTimeout(finish, TRANSITION_FALLBACK);
  }

  /** After collapsing a long group, bring its top back into view so the reader isn't stranded below it. */
  #keepInView() {
    const group = this.parentElement;
    if (!group) return;

    // Leave room for the sticky header so the top of the group isn't tucked under it.
    const header = document.querySelector('#header-component');
    const offset = (header?.getBoundingClientRect().height ?? 0) + 24;
    const { top } = group.getBoundingClientRect();
    if (top < offset) window.scrollBy({ top: top - offset, behavior: 'smooth' });
  }
}

if (!customElements.get('collapsible-group')) {
  customElements.define('collapsible-group', CollapsibleGroup);
}
